import type { MiloEvent, MiloRef } from './stream';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  refs: Record<string, MiloRef>;
  /** Claude's summary of how it worked the answer out (the panel's thinking line) */
  thinking?: string;
  /** The id of the text and thinking blocks last appended to: a new id starts a new paragraph */
  textId?: string;
  thinkingId?: string;
  /** The answer is still streaming */
  pending?: boolean;
  /** The failure text ended this answer */
  failed?: boolean;
}

export interface ChatState {
  conversationId: string | null;
  messages: ChatMessage[];
  /** The tool the answer waits on, for the progress line */
  progress: string | null;
  busy: boolean;
  remainingToday: number | null;
  /** The chat has grown long: the panel suggests a new one */
  longChat: boolean;
}

export const emptyChat: ChatState = { conversationId: null, messages: [], progress: null, busy: false, remainingToday: null, longChat: false };

export type ChatEvent =
  | { type: 'ask'; id: string; text: string }
  | { type: 'event'; event: MiloEvent }
  | { type: 'refused' }
  | { type: 'done' }
  | { type: 'load'; conversationId: string; messages: ChatMessage[] }
  | { type: 'reset' };

/** The answer being written: the last message, while it is pending */
function withAnswer(state: ChatState, change: (answer: ChatMessage) => ChatMessage): ChatState {
  const last = state.messages.at(-1);
  if (!last || last.role !== 'assistant' || !last.pending) return state;
  return { ...state, messages: [...state.messages.slice(0, -1), change(last)] };
}

export function chatReducer(state: ChatState, event: ChatEvent): ChatState {
  switch (event.type) {
    case 'ask':
      return {
        ...state,
        busy: true,
        progress: null,
        messages: [...state.messages, { id: event.id, role: 'user', text: event.text, refs: {} }, { id: `${event.id}-answer`, role: 'assistant', text: '', refs: {}, pending: true }],
      };
    case 'refused':
      // refused before any answer: neither the question nor the empty answer stays (the question goes back to the input)
      return { ...state, busy: false, progress: null, messages: state.messages.slice(0, -2) };
    case 'done': {
      const ended = withAnswer(state, (answer) => ({ ...answer, pending: false }));
      return { ...ended, busy: false, progress: null };
    }
    case 'load':
      return { ...emptyChat, remainingToday: state.remainingToday, conversationId: event.conversationId, messages: event.messages };
    case 'reset':
      return { ...emptyChat, remainingToday: state.remainingToday };
    case 'event':
      return eventReducer(state, event.event);
  }
}

function eventReducer(state: ChatState, event: MiloEvent): ChatState {
  switch (event.type) {
    case 'start': {
      const named = withAnswer(state, (answer) => ({ ...answer, id: event.messageId }));
      return { ...named, conversationId: event.conversationId };
    }
    case 'tool':
      return { ...state, progress: event.name };
    case 'text':
      return {
        ...withAnswer(state, (answer) => ({
          ...answer,
          text: (answer.text !== '' && answer.textId !== event.id ? `${answer.text}\n\n` : answer.text) + event.delta,
          textId: event.id,
        })),
        progress: null,
      };
    case 'thinking':
      return withAnswer(state, (answer) => ({
        ...answer,
        thinking: (answer.thinking && answer.thinkingId !== event.id ? `${answer.thinking}\n\n` : (answer.thinking ?? '')) + event.delta,
        thinkingId: event.id,
      }));
    case 'refs':
      return withAnswer(state, (answer) => ({ ...answer, refs: { ...answer.refs, ...event.refs } }));
    case 'limit':
      return { ...state, remainingToday: event.remainingToday, longChat: event.longChat ?? false };
    case 'error':
      return withAnswer(state, (answer) => ({ ...answer, text: answer.text === '' ? event.message : `${answer.text}\n\n${event.message}`, failed: true }));
    case 'done':
      return state;
  }
}

/** A stored conversation's messages as the panel shows them: the text blocks and the thinking each joined as paragraphs, the refs from its data-refs part */
export function messagesOf(stored: Array<{ id: string; role: 'user' | 'assistant'; parts: unknown[] }>): ChatMessage[] {
  return stored.map((message) => {
    const texts: string[] = [];
    const thinkings: string[] = [];
    let refs: Record<string, MiloRef> = {};
    for (const part of message.parts as Array<{ type?: string; text?: string; thinking?: string; data?: { refs?: Record<string, MiloRef> } }>) {
      if (part.type === 'text' && typeof part.text === 'string') texts.push(part.text);
      // echo stores Claude's thinking as a reasoning part; a raw thinking block is read too
      if (part.type === 'reasoning' && typeof part.text === 'string') thinkings.push(part.text);
      if (part.type === 'thinking' && typeof (part.thinking ?? part.text) === 'string') thinkings.push((part.thinking ?? part.text)!);
      if (part.type === 'data-refs' && part.data?.refs) refs = { ...refs, ...part.data.refs };
    }
    const thinking = thinkings.filter((piece) => piece !== '').join('\n\n');
    return { id: message.id, role: message.role, text: texts.join('\n\n'), refs, ...(thinking !== '' ? { thinking } : {}) };
  });
}

/** The language milo answers in by default: the UI's, Serbian for anything else */
export function miloLanguage(language: string): 'sr' | 'en' | 'es' {
  const base = language.slice(0, 2);
  return base === 'en' || base === 'es' ? base : 'sr';
}
